import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import { styled } from '@mui/material/styles';
import Switch from '@/components/Switch/Switch';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import SaveIcon from '@/assets/save-floppy-disk.svg?react';
import {
  fetchFilters,
  putUser,
  postUserPermissions,
  deleteUserPermissions,
} from '@/lib/api';
import { ContactSchema } from '@/lib/types';
import { getPermissionIdsByKeys } from '@/lib/permissions';
import {
  EDIT_CONTACTS_PERMISSIONS,
  VIEW_COMPLIANCE_DOCUMENTS_PERMISSIONS,
} from '@/constants/Permissions';

const MassUpdateModal = ({
  open,
  onClose,
  onSave,
  selectedUsers,
}: {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  selectedUsers: ContactSchema[];
}) => {
  const [viewComplianceDocs, setViewComplianceDocs] =
    useState<boolean>(false);
  const [editContacts, setEditContacts] = useState<boolean>(false);

  const { data: filtersData } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });

  const { mutateAsync: mutatePutAsync, isPending: isUpdatingName } =
    useMutation({
      mutationFn: putUser,
    });

  const {
    mutateAsync: mutatePostPermissionsAsync,
    isPending: isAddingPermissions,
  } = useMutation({
    mutationFn: postUserPermissions,
  });

  const {
    mutateAsync: mutateDeletePermissionsAsync,
    isPending: isDeletingPermissions,
  } = useMutation({
    mutationFn: deleteUserPermissions,
  });

  const isPending =
    isUpdatingName || isAddingPermissions || isDeletingPermissions;

  const handleClose = () => {
    setViewComplianceDocs(false);
    setEditContacts(false);
    onClose();
  };

  const handleSave = async () => {
    const viewComplianceDocsIds = getPermissionIdsByKeys(
      filtersData?.permissions || [],
      VIEW_COMPLIANCE_DOCUMENTS_PERMISSIONS,
    );
    const editContactsIds = getPermissionIdsByKeys(
      filtersData?.permissions || [],
      EDIT_CONTACTS_PERMISSIONS,
    );

    const updatePromises = selectedUsers.map(async (user) => {
      let permissionIds: number[] =
        user.permissions?.map((p) => p.id) || [];

      if (viewComplianceDocs) {
        permissionIds = [
          ...permissionIds,
          ...viewComplianceDocsIds.filter(
            (id) => !permissionIds.includes(id),
          ),
        ];
      } else {
        permissionIds = permissionIds.filter(
          (id) => !viewComplianceDocsIds.includes(id),
        );
      }

      if (editContacts) {
        permissionIds = [
          ...permissionIds,
          ...editContactsIds.filter(
            (id) => !permissionIds.includes(id),
          ),
        ];
      } else {
        permissionIds = permissionIds.filter(
          (id) => !editContactsIds.includes(id),
        );
      }

      await mutatePutAsync({
        user_id: user.id,
        name: user.name,
      });

      const currentPermissionIds =
        user.permissions?.map((p) => p.id) || [];
      const permissionsToAdd = permissionIds.filter(
        (id) => !currentPermissionIds.includes(id),
      );
      const permissionsToDelete = currentPermissionIds.filter(
        (id) => !permissionIds.includes(id),
      );

      const permissionPromises: Promise<unknown>[] = [];

      if (permissionsToAdd.length > 0) {
        permissionPromises.push(
          mutatePostPermissionsAsync({
            user_id: user.id,
            permission_ids: permissionsToAdd,
          }),
        );
      }

      if (permissionsToDelete.length > 0) {
        permissionPromises.push(
          mutateDeletePermissionsAsync({
            user_id: user.id,
            permission_ids: permissionsToDelete,
          }),
        );
      }

      await Promise.all(permissionPromises);
    });

    try {
      await Promise.all(updatePromises);
      onSave();
      handleClose();
    } catch (error) {
      console.error('Mass update failed:', error);
    }
  };

  const handleSwitchChange =
    (setter: (value: boolean) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.checked);
    };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        <FormattedMessage
          id="massUpdateContacts"
          defaultMessage="Mass Update Contacts"
        />
      }
      actions={[
        <Button
          key="close"
          variant="text"
          onClick={handleClose}
          aria-label="Cancel Button"
        >
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>,
        <Button
          key="save"
          variant="contained"
          onClick={handleSave}
          disabled={isPending}
          loading={isPending}
          aria-label="Save Button"
        >
          <SaveIcon />
          <FormattedMessage id="save" defaultMessage="Save" />
        </Button>,
      ]}
    >
      <Description>
        <FormattedMessage
          id="massUpdateDescription"
          defaultMessage="Apply changes in bulk to the ({count}) selected contacts"
          values={{ count: selectedUsers.length }}
        />
      </Description>
      <Label>
        <FormattedMessage
          id="permissions"
          defaultMessage="Permissions"
        />
      </Label>
      <SwitchContainer>
        <SwitchWrapper>
          <Switch
            checked={viewComplianceDocs === true}
            onChange={handleSwitchChange(setViewComplianceDocs)}
            id="viewComplianceDocs"
            aria-label="View Compliance Documents"
          />
          <SwitchLabel htmlFor="viewComplianceDocs">
            <FormattedMessage
              id="viewComplianceDocuments"
              defaultMessage="View Compliance Documents"
            />
          </SwitchLabel>
        </SwitchWrapper>
        <SwitchWrapper>
          <Switch
            checked={editContacts === true}
            onChange={handleSwitchChange(setEditContacts)}
            id="editContacts"
            aria-label="Edit Contacts"
          />
          <SwitchLabel htmlFor="editContacts">
            <FormattedMessage
              id="editContacts"
              defaultMessage="Edit Contacts"
            />
          </SwitchLabel>
        </SwitchWrapper>
      </SwitchContainer>
    </Modal>
  );
};

const Description = styled('div')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  marginBottom: theme.spacing(3),
  color: theme.palette.text.secondary,
}));

const Label = styled('div')(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const SwitchContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const SwitchWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const SwitchLabel = styled('label')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  cursor: 'pointer',
  userSelect: 'none',
  color: theme.palette.text.primary,
  marginLeft: theme.spacing(1),
}));

export default MassUpdateModal;
